import { baseApi, TAGS } from 'shared/api';
import type { BaseResponse } from 'shared/models/common';

import buildFilesFormData from 'shared/utils/buildFilesFormData';

import type { Folder } from 'shared/models/folder';
import type { DocumentsOptions, GetRequestParams } from './types';
import type { UploadFormData } from './components/UploadForm/UploadForm.types';
import type { EditDocumentFormData } from './components/FiltersBar/EditButton/EditDocumentForm/EditDocumentForm.types';

import { isMaintenance, transformResponse } from './helpers';

type BaseEndpointParams = {
  cooperativeId: string;
  portalName: string;
};

function getOptionsEndpoint(params: BaseEndpointParams) {
  const { cooperativeId, portalName } = params;

  let baseUrl = '/user';

  if (isMaintenance(portalName)) {
    baseUrl += '/maintenance';
  }

  return baseUrl.concat(`/cooperatives/${cooperativeId}/documents/options`);
}

function getDocumentsEndpoint(params: BaseEndpointParams) {
  const { cooperativeId, portalName } = params;

  let baseUrl = `/user/cooperatives/${cooperativeId}/documents`;

  if (isMaintenance(portalName)) {
    return `${baseUrl}/account/${cooperativeId}`;
  }

  return baseUrl;
}

function uploadDocumentsEndpoint(params: BaseEndpointParams) {
  const { cooperativeId, portalName } = params;

  let baseUrl = '/user';

  if (isMaintenance(portalName)) {
    baseUrl += '/maintenance';
  }

  return baseUrl.concat(`/cooperatives/${cooperativeId}/documents`);
}

export function downloadDocumentEndpoint(params: BaseEndpointParams) {
  const { cooperativeId, portalName } = params;

  let baseUrl = `/api/user/cooperatives/${cooperativeId}/documents`;

  if (isMaintenance(portalName)) {
    return `${baseUrl}/account/${cooperativeId}/download`;
  }

  return `${baseUrl}/download`;
}

function deleteEntityEndpoint(params: BaseEndpointParams) {
  const { cooperativeId, portalName } = params;

  let baseUrl = `/user/cooperatives/${cooperativeId}/documents`;

  if (isMaintenance(portalName)) {
    return `${baseUrl}/account/${cooperativeId}`;
  }

  return baseUrl;
}

export const documentsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getOptions: build.query<DocumentsOptions, GetRequestParams>({
      query: ({ portalName, coopId: cooperativeId }) => ({
        url: getOptionsEndpoint({ cooperativeId, portalName }),
        method: 'GET',
      }),
      transformResponse,
    }),
    getDocuments: build.query<Folder, GetRequestParams>({
      query: ({ coopId: cooperativeId, portalName }) => ({
        url: getDocumentsEndpoint({ cooperativeId, portalName }),
        method: 'GET',
      }),
      providesTags: [TAGS.DOCUMENTS_LIST],
      transformResponse: (response: BaseResponse<Folder[]>) => response.Data[0],
    }),
    uploadDocuments: build.mutation<
      void,
      {
        body: UploadFormData;
        coopId: string;
        portalName: string;
        folderUrl?: string;
      }
    >({
      query: (params) => {
        const { body, coopId: cooperativeId, portalName, folderUrl } = params;
        console.log(params, buildFilesFormData(body.files));

        return {
          url: uploadDocumentsEndpoint({ cooperativeId, portalName }),
          method: 'POST',
          body: buildFilesFormData(body.files),
          params: {
            ...(folderUrl && { folderUrl }),
            informationGroups: body.informationGroups,
            serviceProcesses: body.serviceProcesses,
            viewingRights: body.viewingRights,
          },
        };
      },
      invalidatesTags: (result) => (result ? [TAGS.DOCUMENTS_LIST] : []),
    }),
    createFolder: build.mutation<
      void,
      { coopId: string; folderName: string; parentFolderUrl?: string }
    >({
      query: ({ coopId: cooperativeId, folderName, parentFolderUrl }) => ({
        url: `/user/cooperatives/${cooperativeId}/folders`,
        method: 'POST',
        params: {
          folderName,
          ...(parentFolderUrl && { parentFolderUrl }),
        },
      }),
      invalidatesTags: (_, error) => (error ? [] : [TAGS.DOCUMENTS_LIST]),
    }),
    deleteEntity: build.mutation<
      void,
      {
        coopId: string;
        portalName: string;
        url: string;
        type: 'documents' | 'folders';
      }
    >({
      query: ({ coopId: cooperativeId, portalName, type, url }) => ({
        url: deleteEntityEndpoint({ cooperativeId, portalName }),
        method: 'DELETE',
        params: {
          [type === 'documents' ? 'fileUrl' : 'folderUrl']: url,
        },
      }),
      invalidatesTags: (_, error) => (error ? [] : [TAGS.DOCUMENTS_LIST]),
    }),
    deleteFolder: build.mutation<void, { coopId: string; folderUrl: string }>({
      query: ({ coopId, folderUrl }) => ({
        url: `/user/cooperatives/${coopId}/folders`,
        method: 'DELETE',
        params: {
          folderUrl,
        },
      }),
      invalidatesTags: (_, error) => (error ? [] : [TAGS.DOCUMENTS_LIST]),
    }),
    editFolder: build.mutation<
      void,
      { coopId: string; folderUrl: string; newFolderName: string }
    >({
      query: ({ coopId, folderUrl, newFolderName }) => ({
        url: `/user/cooperatives/${coopId}/folders`,
        method: 'PUT',
        params: {
          folderUrl,
          newFolderName,
        },
      }),
      invalidatesTags: (_, error) => (error ? [] : [TAGS.DOCUMENTS_LIST]),
    }),
    editDocument: build.mutation<
      void,
      {
        body: EditDocumentFormData;
        coopId: string;
        fileUrl: string;
      }
    >({
      query: ({ body, coopId, fileUrl }) => ({
        url: `/user/cooperatives/${coopId}/documents`,
        method: 'PUT',
        params: {
          fileUrl,
          informationGroups: body.informationGroups,
          newFileName: body.documentName,
          serviceProcesses: body.serviceProcesses,
          viewingRights: body.viewingRights,
        },
      }),
      invalidatesTags: (_, error) => (error ? [] : [TAGS.DOCUMENTS_LIST]),
    }),
  }),
});
